import { DOUBLING_VERBS } from './resume-verbs';

/**
 * T.10 S15 (D96). Resume skill phrases from O*NET work activities, pure.
 *
 * A picked task's Detailed Work Activity reads as an "-ing" phrase
 * ("Resolve computer software problems" to "Resolving computer software
 * problems"); without one, the task's General Work Activity gives the phrase
 * below. The same table names the work activities of a held job profile
 * (SkillsDNA Tier 1 `work_activity` elements), which the military profiles
 * carry. The copy is Bethany's to edit, like the question table (spec 9.4).
 */
export const ACTIVITY_PHRASES: Readonly<Record<string, string>> = {
  '4.A.1.a.1': 'Research and fact-finding',
  '4.A.1.a.2': 'Monitoring operations and equipment',
  '4.A.1.b.1': 'Inspection and observation',
  '4.A.1.b.2': 'Equipment inspection',
  '4.A.1.b.3': 'Estimating time, cost and materials',
  '4.A.2.a.1': 'Quality assessment',
  '4.A.2.a.2': 'Records and data processing',
  '4.A.2.a.3': 'Compliance and standards review',
  '4.A.2.a.4': 'Data analysis',
  '4.A.2.b.1': 'Problem solving and decision making',
  '4.A.2.b.2': 'Process improvement',
  '4.A.2.b.3': 'Keeping technical knowledge current',
  '4.A.2.b.4': 'Planning and goal setting',
  '4.A.2.b.5': 'Scheduling',
  '4.A.2.b.6': 'Organizing and prioritizing work',
  '4.A.3.a.1': 'Hands-on physical work',
  '4.A.3.a.2': 'Material handling',
  '4.A.3.a.3': 'Machine and process control',
  '4.A.3.a.4': 'Vehicle and equipment operation',
  '4.A.3.b.1': 'Computer systems and software',
  '4.A.3.b.2': 'Technical drawings and specifications',
  '4.A.3.b.4': 'Mechanical repair and maintenance',
  '4.A.3.b.5': 'Electronic equipment repair',
  '4.A.3.b.6': 'Documentation and record keeping',
  '4.A.4.a.1': 'Explaining technical information',
  '4.A.4.a.2': 'Team communication',
  '4.A.4.a.3': 'Communicating with customers and partners',
  '4.A.4.a.4': 'Building working relationships',
  '4.A.4.a.5': 'Assisting and supporting people',
  '4.A.4.a.6': 'Sales and persuasion',
  '4.A.4.a.7': 'Conflict resolution and negotiation',
  '4.A.4.a.8': 'Customer service',
  '4.A.4.b.1': 'Coordinating work across teams',
  '4.A.4.b.2': 'Team building',
  '4.A.4.b.3': 'Training and instruction',
  '4.A.4.b.4': 'Team leadership',
  '4.A.4.b.5': 'Coaching and mentoring',
  '4.A.4.b.6': 'Technical advice and consultation',
  '4.A.4.c.1': 'Administrative support',
  '4.A.4.c.2': 'Staffing and recruiting',
  '4.A.4.c.3': 'Resource and budget management'
};

/** Claims about the member a job profile alone never makes (D96): only from their own tasks. */
export const OWN_EVIDENCE_ONLY: ReadonlySet<string> = new Set(['4.A.4.b.2', '4.A.4.b.4', '4.A.4.b.5', '4.A.4.c.2', '4.A.4.c.3']);

/** At most this many phrases come from one task. */
export const PHRASES_PER_TASK = 2;
const MAX_WORDS = 8;

export function activityPhrase(gwaId: string): string | null {
  return ACTIVITY_PHRASES[gwaId] ?? null;
}

/** "resolve" to "resolving", "set" to "setting", "tie" to "tying". */
export function gerundOf(verb: string): string {
  const w = verb.toLowerCase();
  if (w.endsWith('ie')) {
    return `${w.slice(0, -2)}ying`;
  }
  if (/(?:ee|ye|oe)$/.test(w)) {
    return `${w}ing`;
  }
  if (w.endsWith('e') && w.length > 2) {
    return `${w.slice(0, -1)}ing`;
  }
  if (DOUBLING_VERBS.has(w) || /^[^aeiou]*[aeiou][^aeiouwxy]$/.test(w)) {
    return `${w}${w.slice(-1)}ing`;
  }
  return `${w}ing`;
}

const CUTS = [', ', ' such as ', ' including ', ' in order to', ' using ', ' according to', ' in accordance with', '(', ';', ' to '];
const TRAILING = new Set(['a', 'an', 'the', 'and', 'or', 'of', 'to', 'for', 'in', 'on', 'at', 'by', 'with', 'from', 'into', 'about', 'as', 'their', 'its', 'other', 'all', 'any', 'each']);

/** A Detailed Work Activity as a resume skill phrase: cut like a chip label (spec 9.3), its verb as "-ing", at most 8 words. */
export function dwaPhrase(dwa: string): string {
  const bare = (dwa ?? '').trim().replace(/\.$/, '');
  if (!/[A-Za-z]/.test(bare)) {
    return '';
  }
  let cut = bare.length;
  for (const c of CUTS) {
    const at = bare.indexOf(c);
    if (at > 0 && at < cut && bare.slice(0, at).trim().split(/\s+/).length >= 3) {
      cut = at;
    }
  }
  let words = bare.slice(0, cut).trim().split(/\s+/).slice(0, MAX_WORDS);
  while (words.length > 1 && TRAILING.has(words[words.length - 1].toLowerCase())) {
    words = words.slice(0, -1);
  }
  words[0] = gerundOf(words[0]);
  const out = words.join(' ');
  return out.charAt(0).toUpperCase() + out.slice(1);
}

/** A task's phrases: its DWA phrases (once each, at most two), else the phrase of its most important work activity. */
export function taskSkills(dwaLabels: readonly string[], gwaIds: readonly string[], importance: ReadonlyMap<string, number> = new Map()): string[] {
  const out: string[] = [];
  for (const d of dwaLabels) {
    const p = dwaPhrase(d);
    if (p && !out.some((x) => x.toLowerCase() === p.toLowerCase())) {
      out.push(p);
    }
  }
  if (out.length) {
    return out.slice(0, PHRASES_PER_TASK);
  }
  const best = [...gwaIds].filter((g) => ACTIVITY_PHRASES[g]).sort((a, b) => (importance.get(b) ?? 0) - (importance.get(a) ?? 0))[0];
  return best ? [ACTIVITY_PHRASES[best]] : [];
}
